import { useEffect, useState } from 'react';
import badge from '../../assets/badge.svg';
import perform from '../../assets/performance.png';
import Nav from '../../components/Nav';
import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';

const STAR_HOST_TARGETS = {
  reponses: 95,
  acceptation: 90,
  engagement: 95,
  evaluation: 90,
  voyages: 10,
};

function PerformanceGauge({ id, percent }) {
  const pct = Math.min(Math.max(percent || 0, 0), 100);

  const radius = 42;
  const circumference = Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 140,
        margin: '0 auto',
        position: 'relative',
      }}
    >
      <svg viewBox="0 0 100 58" width="100%" aria-label={`Performance ${pct}%`} role="img">
        <defs>
          <linearGradient id={`gauge-gradient-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#22c55e" />
          </linearGradient>
        </defs>

        <path
          d="M 8 50 A 42 42 0 0 1 92 50"
          fill="none"
          stroke="#e9ecef"
          strokeWidth="10"
          strokeLinecap="round"
        />

        <path
          d="M 8 50 A 42 42 0 0 1 92 50"
          fill="none"
          stroke={`url(#gauge-gradient-${id})`}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
    </div>
  );
}

function MetricCard({ id, icon, color, title, percent, target, description, detail }) {
  const pct = Math.min(Math.max(percent || 0, 0), 100);
  const isGood = pct >= target;

  return (
    <div className="card border-0 shadow-sm rounded-3 h-100">
      <div className="card-body p-2 p-sm-3">
        {/* Icône + titre */}
        <div className="d-flex align-items-center gap-2 mb-1">
          <div
            className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
            style={{ width: 28, height: 28, background: `${color}18` }}
          >
            <i className={`bi ${icon}`} style={{ color, fontSize: 13 }}></i>
          </div>
          <p className="mb-0 fw-semibold lh-sm" style={{ fontSize: 12 }}>
            {title}
          </p>
        </div>

        {/* Badge statut sur sa propre ligne */}
        <div className="mb-2">
          <span
            className={`badge rounded-pill ${isGood ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}
            style={{ fontSize: 10 }}
          >
            {isGood ? '✓ Objectif' : 'En progrès'}
          </span>
        </div>

        {/* Jauge */}
        <div style={{ maxWidth: 140, margin: '0 auto' }}>
          <PerformanceGauge id={id} percent={pct} />
        </div>

        {/* Score */}
        <div className="text-center mb-2">
          <span className="fw-bold" style={{ fontSize: 22, color }}>
            {pct}
            <span style={{ fontSize: 12, color: '#6c757d' }}>%</span>
          </span>
          <p className="mb-0 text-muted" style={{ fontSize: 10 }}>
            Cible : <strong>{target}%</strong>
          </p>
        </div>

        {/* Barre de progression vs objectif */}
        <div className="mb-2">
          <div className="progress" style={{ height: 4, borderRadius: 99 }}>
            <div
              className={`progress-bar ${isGood ? 'bg-success' : 'bg-warning'}`}
              style={{ width: `${Math.min((pct / target) * 100, 100)}%`, borderRadius: 99 }}
            />
          </div>
        </div>

        {/* Description — masquée sur mobile pour éviter l'overflow */}
        <p className="text-muted mb-0 d-none d-sm-block" style={{ fontSize: 10 }}>
          {description}
        </p>
      </div>
    </div>
  );
}

function AccountPerformances() {
  const axios = useAxios();

  const { user, loading, isAuthenticated } = useUser();

  const [TauxAcceptation, setTauxAcceptation] = useState(0);
  const [TauxEngagement, setTauxEngagement] = useState(0);
  const [EvaluationMax, setEvaluationMax] = useState(0);
  const [TauxReponses, setTauxReponses] = useState(0);
  const [VoyagesFinalises, setVoyagesFinalises] = useState(0);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    // Seed from localStorage while live data loads
    setTauxAcceptation(user.AcceptanceRate || 0);
    setTauxEngagement(user.EngagementRate || 0);
    setEvaluationMax((user.EvaluationNumber || 0) * 100);
    setTauxReponses(user.ResponseRate || 0);
    setVoyagesFinalises(user.Finalizedtrips || 0);

    // Fetch live computed rates from backend
    axios
      .get('/users/' + user.id + '/performance')
      .then(({ data }) => {
        setTauxAcceptation(data.acceptanceRate ?? 0);
        setTauxReponses(data.responseRate ?? 0);
        setTauxEngagement(data.engagementRate ?? 0);
        setVoyagesFinalises(data.finalizedtrips ?? 0);
      })
      .catch(() => {});

    axios
      .get('/reservations/getbydriverid/' + user.id)
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setReservations(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        }
      })
      .catch(() => {});
  }, []);

  const totalResa = reservations.length;
  const accepted = reservations.filter((r) =>
    ['accepted', 'paid', 'completed'].includes(r.status)
  ).length;
  const completed = reservations.filter((r) => r.status === 'completed').length;
  const pending = reservations.filter((r) => r.status === 'pending').length;

  /* Score global simplifié */
  const globalScore = Math.round(
    (TauxReponses + TauxAcceptation + TauxEngagement + EvaluationMax) / 4
  );
  const isStarHost = globalScore >= 90;

  const metrics = [
    {
      id: 'reponses',
      icon: 'bi-reply-fill',
      color: '#0d6efd',
      title: 'Taux de réponse',
      percent: TauxReponses,
      target: STAR_HOST_TARGETS.reponses,
      description: 'Répondez rapidement aux demandes pour améliorer votre classement.',
      detail: `Objectif Hôte Star : ${STAR_HOST_TARGETS.reponses}%`,
    },
    {
      id: 'acceptation',
      icon: 'bi-check-circle-fill',
      color: '#22c55e',
      title: "Taux d'acceptation",
      percent: TauxAcceptation,
      target: STAR_HOST_TARGETS.acceptation,
      description: `${accepted} demande(s) acceptée(s) sur ${totalResa}.`,
      detail: `Objectif Hôte Star : ${STAR_HOST_TARGETS.acceptation}%`,
    },
    {
      id: 'engagement',
      icon: 'bi-flag-fill',
      color: '#8b5cf6',
      title: "Taux d'engagement",
      percent: TauxEngagement,
      target: STAR_HOST_TARGETS.engagement,
      description: `${completed} voyage(s) finalisé(s) sur ${totalResa}.`,
      detail: `Objectif Hôte Star : ${STAR_HOST_TARGETS.engagement}%`,
    },
    {
      id: 'evaluation',
      icon: 'bi-star-fill',
      color: '#f59e0b',
      title: 'Évaluations 5 étoiles',
      percent: EvaluationMax,
      target: STAR_HOST_TARGETS.evaluation,
      description: 'Part des voyageurs vous ayant attribué 5 étoiles.',
      detail: `Objectif Hôte Star : ${STAR_HOST_TARGETS.evaluation}%`,
    },
    {
      id: 'voyages',
      icon: 'bi-car-front-fill',
      color: '#06b6d4',
      title: 'Voyages finalisés',
      percent: Math.min((VoyagesFinalises / 10) * 100, 100),
      target: 100,
      description: `${VoyagesFinalises} voyage(s) terminé(s). Objectif : 10.`,
    },
  ];

  return (
    <section className="bg-body-tertiary min-vh-100">
      <div className="container py-0 pb-4">
        <Nav />

        {/* Hero score global */}
        <div
          className="rounded-3 p-3 p-md-4 mb-4 text-white"
          style={{
            background: isStarHost
              ? 'linear-gradient(135deg,#0d6efd,#6610f2)'
              : 'linear-gradient(135deg,#6c757d,#495057)',
          }}
        >
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25"
              style={{ width: 56, height: 56, flexShrink: 0 }}
            >
              <img src={badge} alt="badge" style={{ width: 32 }} />
            </div>
            <div className="flex-grow-1">
              <p className="mb-0 fw-bold fs-5">{isStarHost ? '🌟 Hôte Star' : 'Hôte standard'}</p>
              <p className="mb-0 opacity-75" style={{ fontSize: 13 }}>
                Score global : <strong>{globalScore}%</strong> · {totalResa} réservation(s) ·{' '}
                {completed} voyage(s) finalisé(s)
              </p>
            </div>
            <button
              className="btn btn-sm btn-light d-md-none"
              data-bs-toggle="offcanvas"
              data-bs-target="#tipsOffcanvas"
            >
              <i className="bi bi-lightbulb me-1"></i>Conseils
            </button>
          </div>

          {/* Barre score global */}
          <div className="mt-3">
            <div className="d-flex justify-content-between mb-1">
              <small className="opacity-75">Score performance</small>
              <small className="fw-bold">{globalScore} / 100</small>
            </div>
            <div
              className="progress"
              style={{ height: 8, borderRadius: 99, background: 'rgba(255,255,255,.2)' }}
            >
              <div
                className="progress-bar bg-white"
                style={{ width: `${globalScore}%`, borderRadius: 99 }}
              />
            </div>
          </div>

          {/* KPI chips */}
          <div className="d-flex gap-2 mt-3 flex-wrap">
            {[
              { label: 'En attente', value: pending, color: 'bg-warning text-dark' },
              { label: 'Acceptées', value: accepted, color: 'bg-success text-white' },
              { label: 'Finalisées', value: completed, color: 'bg-white text-dark' },
            ].map(({ label, value, color }) => (
              <span
                key={label}
                className={`badge rounded-pill ${color}`}
                style={{ fontSize: 12, padding: '5px 10px' }}
              >
                {value} {label}
              </span>
            ))}
          </div>
        </div>

        {/* Offcanvas conseils (mobile) */}
        <div
          className="offcanvas offcanvas-end"
          tabIndex="-1"
          id="tipsOffcanvas"
          style={{ maxWidth: 340 }}
        >
          <div className="offcanvas-header border-bottom">
            <h6 className="offcanvas-title fw-bold">
              <i className="bi bi-lightbulb me-2 text-warning"></i>Conseils performance
            </h6>
            <button type="button" className="btn-close" data-bs-dismiss="offcanvas" />
          </div>
          <div className="offcanvas-body">
            <TipsContent badge={badge} perform={perform} />
          </div>
        </div>

        {/* Contenu principal */}
        <div className="row g-3">
          {/* Métriques */}
          <div className="col-lg-8">
            <div className="row g-3">
              {metrics.map((m) => (
                <div key={m.id} className="col-6 col-sm-6 col-md-4">
                  <MetricCard {...m} />
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar desktop */}
          <div className="col-lg-4 d-none d-lg-block">
            <TipsContent badge={badge} perform={perform} />
          </div>
        </div>
      </div>
    </section>
  );
}

function TipsContent({ badge, perform }) {
  return (
    <div className="d-flex flex-column gap-3">
      {/* Programme Hôte Star */}
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-body p-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <img src={badge} alt="badge" style={{ width: 36 }} />
            <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
              Programme Hôte Star
            </p>
          </div>
          <p className="text-muted mb-2" style={{ fontSize: 12 }}>
            Profitez d'avantages exclusifs pour célébrer votre excellente performance.
          </p>
          <a href="#" className="btn btn-sm btn-outline-primary w-100" style={{ fontSize: 12 }}>
            En savoir plus
          </a>
          <div className="alert alert-info mb-0 mt-2 py-2 px-2" style={{ fontSize: 11 }}>
            <i className="bi bi-calendar-event me-1"></i>
            Prochaine évaluation : <strong>15 avr. 2025</strong>
          </div>
        </div>
      </div>

      {/* Conseils */}
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-body p-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <img src={perform} alt="performance" style={{ width: 28 }} />
            <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
              Conseils pour progresser
            </p>
          </div>
          <div className="d-flex flex-column gap-2">
            {[
              {
                icon: 'bi-calendar-check',
                text: 'Gardez votre calendrier à jour pour recevoir les bonnes demandes.',
              },
              {
                icon: 'bi-sliders',
                text: 'Ajustez vos préférences de réservation (préavis, durée min.).',
              },
              { icon: 'bi-bell', text: 'Activez les notifications pour répondre rapidement.' },
              { icon: 'bi-info-circle', text: "Consultez le guide complet d'évaluation Moovicar." },
            ].map(({ icon, text }, i) => (
              <div key={i} className="d-flex gap-2 align-items-start">
                <i
                  className={`bi ${icon} text-primary flex-shrink-0 mt-1`}
                  style={{ fontSize: 13 }}
                ></i>
                <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountPerformances;
